// Quem está logado, disponível em toda a árvore.
//
// O nível fica aqui para a interface decidir o que DESENHAR. Ele não autoriza
// nada: quem autoriza é a API, em toda requisição, contra o banco. Mostrar um
// botão a mais aqui não dá acesso a nada, e esconder um não protege nada — é
// por isso que o servidor nunca confia neste valor.

import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { quandoPerderSessao } from "../api/cliente";
import { deposito } from "../api/deposito";
import * as sessaoAPI from "../api/sessao";
import type { Nivel, Sessao } from "../api/tipos";
import { peloMenos } from "../api/tipos";
import { trocarDeIdentidade } from "./cache";

interface Contexto {
  sessao: Sessao | null;
  carregando: boolean;
  definir: (s: Sessao) => void;
  sair: () => Promise<void>;
  /** "esta pessoa é pelo menos revenda?" — só para escolher o que mostrar. */
  podeVer: (minimo: Nivel) => boolean;
  /**
   * Sai da caixa do cliente e volta a ser quem você é.
   *
   * NÃO guarda a sessão do administrador em lugar nenhum: o cookie de
   * renovação dele nunca foi tocado (a sessão de personificação não grava
   * cookie), então `retomar()` a reconstrói sozinho. Guardar o token
   * anterior em memória seria uma segunda cópia da identidade de alguém
   * circulando sem necessidade.
   */
  voltarAoPainel: () => Promise<void>;

  /**
   * As outras contas abertas NESTE navegador, com a vaga de cada uma.
   *
   * Vem do servidor porque o índice de vagas é um cookie HttpOnly — este
   * código não pode lê-lo, e é esse o ponto. Ver api/interno/web/cookie.go.
   */
  contas: sessaoAPI.ContaAberta[];

  /** Troca ESTA ABA para outra conta já aberta, sem pedir senha. */
  trocarParaVaga: (vaga: number) => Promise<void>;

  /**
   * Mostra o login SEM derrubar a conta atual.
   *
   * É o que permite abrir uma segunda conta: a nova entra numa vaga própria,
   * e a aba ao lado — que está noutra vaga — não é tocada. Sair, aqui, seria
   * o oposto: apagaria o cookie da conta atual.
   */
  adicionando: boolean;
  adicionarConta: () => void;
  cancelarAdicao: () => void;
}

const SessaoContexto = createContext<Contexto | null>(null);

export function ProvedorDeSessao({ children }: { children: ReactNode }) {
  const [sessao, setSessao] = useState<Sessao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [contas, setContas] = useState<sessaoAPI.ContaAberta[]>([]);
  const [adicionando, setAdicionando] = useState(false);
  const fila = useQueryClient();

  // QUEM ESTÁ LOGADO MUDA POR UM LUGAR SÓ, e esse lugar sempre apaga o cache
  // da conta anterior. Ver cache.ts — o defeito que isto corrige é o e-mail
  // de uma pessoa aparecendo na tela de outra.
  //
  // O `setSessao` cru NÃO é usado em lugar nenhum fora daqui: eram quatro
  // chamadas diretas (retomar, perder sessão, entrar e sair), e bastava uma
  // delas esquecer de limpar para o vazamento voltar. Agora não há como
  // esquecer, porque não há a segunda forma de trocar.
  //
  // `useRef` para o e-mail anterior, e não o estado: esta função precisa
  // comparar com quem estava logado NO INSTANTE da troca, e o estado dentro
  // de um callback é o do render em que ele foi criado.
  const quemEstava = useRef<string | null>(null);
  const trocarQuem = useCallback(
    (nova: Sessao | null) => {
      // `caixa` é o endereço completo da pessoa logada — é o nome do campo no
      // contrato, e é o que identifica a conta.
      const quem = nova?.caixa ?? null;

      // Cache e credencial trocam no MESMO lugar — ver trocarDeIdentidade,
      // e o defeito que a fez existir.
      trocarDeIdentidade(fila, deposito, quemEstava.current, nova);
      quemEstava.current = quem;
      setSessao(nova);
      // Entrar numa conta nova fecha o modo "adicionar": a aba já é dela.
      setAdicionando(false);
      // A lista de contas muda quando alguém entra ou sai. Reler aqui evita
      // um seletor que mostra conta que não existe mais — e que, clicada,
      // mandaria a pessoa para o login sem explicação.
      void sessaoAPI.contasDoNavegador().then(setContas);
    },
    [fila],
  );

  useEffect(() => {
    let vivo = true;
    // O token de acesso só existe em memória, então todo carregamento começa
    // sem ele. O cookie HttpOnly sobrevive e reconstrói a sessão — inclusive
    // numa aba recém-aberta, porque cookie é compartilhado entre abas.
    sessaoAPI.retomar().then((s) => {
      if (!vivo) return;
      trocarQuem(s);
      setCarregando(false);
    });
    // Quando o cliente desiste de renovar, a aplicação inteira volta ao login.
    // Limpa junto: a sessão caiu, e o que estava em cache é da pessoa que
    // acabou de perdê-la.
    quandoPerderSessao(() => trocarQuem(null));
    return () => {
      vivo = false;
    };
  }, [trocarQuem]);

  const sair = useCallback(async () => {
    await sessaoAPI.sair().catch(() => {});
    trocarQuem(null);
  }, [trocarQuem]);

  // Sair da personificação NÃO chama `sessaoAPI.sair()`, e a diferença é
  // grande: `sair` revoga a sessão e limpa o cookie — o cookie do
  // ADMINISTRADOR, que é o único que existe. Sair assim derrubaria a própria
  // sessão de quem entrou, que é justamente o que todo este desenho evita.
  //
  // A sessão personificada não precisa ser revogada aqui: ela morre em 30
  // minutos de qualquer jeito, e o token dela some da memória junto com a
  // troca abaixo.
  const voltarAoPainel = useCallback(async () => {
    const minha = await sessaoAPI.retomar().catch(() => null);
    trocarQuem(minha);
  }, [trocarQuem]);

  // TROCAR DE CONTA NESTA ABA é mudar a vaga dela e retomar. O cache da conta
  // anterior é apagado por `trocarQuem`, como em qualquer troca de
  // identidade — o defeito de 06/10 não volta por esta porta.
  const trocarParaVaga = useCallback(
    async (vaga: number) => {
      const nova = await sessaoAPI.retomarVaga(vaga).catch(() => null);
      // Falhar aqui NÃO derruba a sessão atual: a vaga pedida pode ter sido
      // encerrada em outra aba, e tirar a pessoa de uma conta viva por causa
      // disso seria trocar um problema pequeno por um grande.
      if (nova) trocarQuem(nova);
      else void sessaoAPI.contasDoNavegador().then(setContas);
    },
    [trocarQuem],
  );

  const valor = useMemo<Contexto>(
    () => ({
      sessao,
      carregando,
      definir: trocarQuem,
      sair,
      voltarAoPainel,
      podeVer: (minimo) => peloMenos(sessao?.nivel, minimo),
      contas,
      trocarParaVaga,
      adicionando,
      adicionarConta: () => setAdicionando(true),
      cancelarAdicao: () => setAdicionando(false),
    }),
    [sessao, carregando, sair, trocarQuem, voltarAoPainel, contas, trocarParaVaga, adicionando],
  );

  return <SessaoContexto.Provider value={valor}>{children}</SessaoContexto.Provider>;
}

export function useSessao(): Contexto {
  const c = useContext(SessaoContexto);
  if (!c) throw new Error("useSessao fora do ProvedorDeSessao");
  return c;
}

/** A sessão, garantidamente presente. Para telas atrás do login. */
export function useCaixa(): Sessao {
  const { sessao } = useSessao();
  if (!sessao) throw new Error("useCaixa sem sessão — a rota deveria estar protegida");
  return sessao;
}
